import { GameState } from './GameState';
import { EventBus, GameEvents } from '../core/EventBus';
import { AttributeKey } from '../data/types';

/**
 * 属性系统
 * 管理六项属性的增减、边界检查、属性变化事件
 */
export class AttributeSystem {
    private static instance: AttributeSystem;

    private constructor() { }

    static getInstance(): AttributeSystem {
        if (!this.instance) {
            this.instance = new AttributeSystem();
        }
        return this.instance;
    }

    /** 增加属性值（自动 clamp 到边界） */
    addAttribute(key: AttributeKey, delta: number): void {
        const state = GameState.getInstance();
        state.addAttribute(key, delta);
    }

    /** 设置属性到指定值 */
    setAttribute(key: AttributeKey, value: number): void {
        const state = GameState.getInstance();
        state.setAttribute(key, value);
    }

    /** 获取当前属性值 */
    getAttribute(key: AttributeKey): number {
        return GameState.getInstance().getAttribute(key);
    }

    /** 获取所有属性 */
    getAllAttributes(): Record<AttributeKey, number> {
        const state = GameState.getInstance();
        return { ...state.player.attributes };
    }

    /** 获取属性平均值 */
    getAverageAttribute(): number {
        const attrs = this.getAllAttributes();
        const values = Object.values(attrs);
        return values.reduce((a, b) => a + b, 0) / values.length;
    }

    /** 获取最高属性 */
    getHighestAttribute(): { key: AttributeKey; value: number } {
        const attrs = this.getAllAttributes();
        let maxKey: AttributeKey = 'charm';
        let maxVal = -1;
        for (const [k, v] of Object.entries(attrs)) {
            if (v > maxVal) {
                maxVal = v;
                maxKey = k as AttributeKey;
            }
        }
        return { key: maxKey, value: maxVal };
    }

    /** 获取最低属性 */
    getLowestAttribute(): { key: AttributeKey; value: number } {
        const attrs = this.getAllAttributes();
        let minKey: AttributeKey = 'charm';
        let minVal = 101;
        for (const [k, v] of Object.entries(attrs)) {
            if (v < minVal) {
                minVal = v;
                minKey = k as AttributeKey;
            }
        }
        return { key: minKey, value: minVal };
    }

    /** 批量应用属性变化（用于行程/事件） */
    /**
     * 施加属性变化（行程/课程的训练收益走这里）。
     * ⚠️ 本函数是**全库唯一一处「训练加属性」的入口**（唯一调用方＝`ScheduleSystem` 执行课程/行程），
     *    所以「XX 减半」类负面特性（九漏鱼/五音不全/低情商）的**训练获得减半**挂在这里：
     *    事件给的、杀青给的、商店买的都不走这条线，不受影响。
     */
    applyChanges(changes: Partial<Record<AttributeKey, number>>): void {
        const state = GameState.getInstance();
        for (const [key, delta] of Object.entries(changes)) {
            if (delta === undefined || delta === 0) continue;
            state.addAttribute(key as AttributeKey, state.trainingGain(key as AttributeKey, delta));
        }
    }
}

/** 导出单例 */
export const attributeSystem = AttributeSystem.getInstance();